import type { DsdEditorValueMatch } from "@/apis/dsd-editor/route";
import type { PlainDsdNoteReferenceStatus } from "@/types/plain-dsd-table";
import type { DsdEditorSelectedCellSummary } from "@/utils/dsd-editor/document-items";
import { useNoteReferenceReviewSelection } from "./use-note-reference-review-selection";
import { useCallback } from "react";
import { EVIDENCE_RESERVE_BELOW_PX } from "./evidence/evidence-layout";

/** 선택한 셀의 하단이 문서 영역 높이의 이 위치에 오도록 스크롤한다 — 그 아래 70% 가 근거 창 자리 */
export const SELECTED_CELL_ALIGN = 0.3;

/**
 * 셀 선택 정책. 상태는 useNoteReferenceReviewSelection 이 들고, 여기서는
 * 정보가 있는 셀만 클릭을 받게 하고, 선택 시 30% 위치로 스크롤하며 패널을 연다.
 */
export const useDsdReviewSelection = ({
  data,
  requestCellScroll,
  onSelected,
}: {
  /** 모드가 고른 매칭 목록과 셀 상태 — 본문·주석매칭은 데이터 훅 값 그대로, 주석·주석매칭은 noteValueMatches + 빈 상태 */
  data: {
    valueMatches: DsdEditorValueMatch[];
    renderedCellIds: ReadonlySet<string>;
    selectedCellSummaryById: ReadonlyMap<string, DsdEditorSelectedCellSummary>;
    noteReferenceStatusByCellId: ReadonlyMap<
      string,
      PlainDsdNoteReferenceStatus
    >;
  };
  requestCellScroll: (
    cellId: string,
    align?: number,
    reserveBelowPx?: number,
  ) => void;
  /** 셀이 선택될 때마다 (패널·첫 후보 근거 창 열기) */
  onSelected: (cellId: string) => void;
}) => {
  // 30% 위치로, 단 그 아래에 근거 창 자리가 모자라면 그만큼 더 올린다 — 스크롤은 이 한 번뿐이다
  const scrollToCell = useCallback(
    (cellId: string) =>
      requestCellScroll(cellId, SELECTED_CELL_ALIGN, EVIDENCE_RESERVE_BELOW_PX),
    [requestCellScroll],
  );
  const selection = useNoteReferenceReviewSelection({
    valueMatches: data.valueMatches,
    renderedCellIds: data.renderedCellIds,
    selectedCellSummaryById: data.selectedCellSummaryById,
    requestCellScroll: scrollToCell,
  });
  const { noteReferenceStatusByCellId } = data;
  const { candidateCellIds, selectCell: selectCellBase } = selection;

  // 헤더·과목명·주석 번호 열은 리뷰 정보가 없어 클릭을 받지 않는다
  const canSelectCell = useCallback(
    (cellId: string) =>
      noteReferenceStatusByCellId.has(cellId) || candidateCellIds.has(cellId),
    [noteReferenceStatusByCellId, candidateCellIds],
  );
  const selectCell = useCallback(
    (cellId: string) => {
      selectCellBase(cellId);
      scrollToCell(cellId);
      onSelected(cellId);
    },
    [selectCellBase, scrollToCell, onSelected],
  );

  return { ...selection, canSelectCell, selectCell, scrollToCell };
};
